import React, { useEffect, useState } from 'react';
import { toast } from '../../../../../ui/toast';
import {
  agenticErrorMessage,
  useGetAgenticReportQuery,
  useRetryAgenticNarrativeMutation,
} from '../../../../../utils/api_service/agenticAssessmentApi';
import { AgenticCompetencyResult, AgenticTurn } from '../../../../../utils/types/agentic_assessment_types';
import { CONFIDENCE_LABEL, LEVEL_NAMES, RECOMMENDATION_CHIP, VERDICT_MARK, formatDate, fullName } from '../constants';

interface ReportViewProps {
  sessionId: string;
  onClose: () => void;
}

/** Level estimate against target on a 0–5 track. */
const LevelBar: React.FC<{ result: AgenticCompetencyResult }> = ({ result }) => {
  const pct = (v: number) => `${(v / 5) * 100}%`;
  return (
    <div className='relative h-2 w-full rounded-full bg-slate-100' aria-hidden>
      <div
          className={`absolute left-0 top-0 h-2 rounded-full ${result.meetsTarget ? 'bg-emerald-500' : 'bg-amber-400'}`}
          style={{ width: pct(result.levelEstimate) }}
      />
      <div className='absolute -top-1 h-4 w-0.5 bg-slate-700' style={{ left: pct(result.targetLevel) }} title={`Target L${result.targetLevel}`} />
    </div>
  );
};

const questionPath = (turns: AgenticTurn[], competencyId: string) =>
  turns.filter((t) => t.competencyId === competencyId && t.evaluation);

export const ReportView: React.FC<ReportViewProps> = ({ sessionId, onClose }) => {
  // The written summary lands a few seconds after the last answer; poll until it does.
  const [waitingForSummary, setWaitingForSummary] = useState(false);
  const { data: report, isLoading } = useGetAgenticReportQuery(sessionId, {
    pollingInterval: waitingForSummary ? 4000 : 0,
    refetchOnMountOrArgChange: true,
  });
  const [retryNarrative, { isLoading: retrying }] = useRetryAgenticNarrativeMutation();

  useEffect(() => {
    const r = report?.result;
    setWaitingForSummary(!!r && !r.narrative && !r.narrativeError);
  }, [report]);

  if (isLoading || !report) {
    return <div className='py-16 text-center text-sm text-slate-500'>Loading the report…</div>;
  }
  const result = report.result;
  const name = report.applicant ? fullName(report.applicant.firstName, report.applicant.lastName) : 'Candidate';
  const notInText = (report.blueprint?.competencies || []).filter((c) => !c.assessableInText);

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <button type='button' onClick={onClose} className='mb-1 text-xs font-semibold text-slate-500 hover:text-slate-800'>
            ← All results
          </button>
          <h3 className='text-lg font-semibold text-slate-900'>{ name }</h3>
          <p className='text-xs text-slate-500'>
            { report.applicant?.email } · completed { formatDate(report.completedAt) } · blueprint v{ report.blueprintVersion } · { report.turns.length } questions
          </p>
        </div>
        { result && (
          <div className='flex items-center gap-3'>
            <div className='text-right'>
              <div className='text-3xl font-semibold tabular-nums text-slate-900'>{ result.overallFit }%</div>
              <div className='text-[11px] uppercase tracking-wide text-slate-500'>Fit Score</div>
            </div>
            <span className={`rounded-full px-3 py-1 text-sm font-semibold ${RECOMMENDATION_CHIP[result.recommendation]}`}>
              { result.recommendation }
            </span>
          </div>
        ) }
      </div>

      { report.endedEarlyAt && (
        <div className='rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
          The candidate ended the interview early. Answered questions were scored; competencies they did not
          get to count as 0 in the fit.
        </div>
      ) }

      { result && result.mustHaveGaps.length > 0 && (
        <div className='rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'>
          Below target on a must-have:{ ' ' }
          { result.competencies.filter((c) => result.mustHaveGaps.includes(c.competencyId)).map((c) => c.name).join(', ') }
        </div>
      ) }

      { result && (
        <section className='flex flex-col gap-2'>
          <h4 className='text-sm font-semibold text-slate-800'>Summary</h4>
          { result.narrative ? (
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
              <p className='text-sm leading-relaxed text-slate-700 lg:col-span-3'>{ result.narrative.summary }</p>
              <div>
                <div className='mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-700'>Strengths</div>
                <ul className='list-disc pl-5 text-sm text-slate-700'>{ result.narrative.strengths.map((s) => <li key={s}>{ s }</li>) }</ul>
              </div>
              <div>
                <div className='mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700'>Concerns</div>
                <ul className='list-disc pl-5 text-sm text-slate-700'>
                  { result.narrative.concerns.length ? result.narrative.concerns.map((s) => <li key={s}>{ s }</li>) : <li>None noted</li> }
                </ul>
              </div>
              <div>
                <div className='mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600'>Ask in the interview</div>
                <ul className='list-disc pl-5 text-sm text-slate-700'>{ result.narrative.followUps.map((s) => <li key={s}>{ s }</li>) }</ul>
              </div>
            </div>
          ) : (
            <div className='flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600'>
              { result.narrativeError ? 'The written summary could not be generated.' : 'Writing the summary…' }
              { result.narrativeError && (
                <button
                    type='button'
                    disabled={retrying}
                    className='text-sm font-semibold text-emerald-700 hover:text-emerald-800 disabled:opacity-50'
                    onClick={async () => {
                      try { await retryNarrative(sessionId).unwrap(); toast.success('Writing the summary again.'); }
                      catch (e) { toast.error(agenticErrorMessage(e, 'Could not retry the summary.')); }
                    }}
                >
                  Try again
                </button>
              ) }
            </div>
          ) }
        </section>
      ) }

      { result && (
        <section className='flex flex-col gap-3'>
          <h4 className='text-sm font-semibold text-slate-800'>Competencies</h4>
          { result.competencies.map((c) => (
            <div key={c.competencyId} className='rounded-xl border border-slate-200 p-4'>
              <div className='flex flex-wrap items-baseline justify-between gap-2'>
                <div className='font-semibold text-slate-900'>
                  { c.name }
                  { c.mustHave && <span className='ml-2 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white'>Must-have</span> }
                </div>
                <div className='text-sm tabular-nums text-slate-600'>
                  L{ c.levelEstimate } vs target L{ c.targetLevel } { LEVEL_NAMES[c.targetLevel] } · { CONFIDENCE_LABEL[c.confidence] }
                </div>
              </div>
              <div className='my-3'><LevelBar result={c} /></div>
              <div className='flex flex-wrap items-center gap-1 text-xs text-slate-600'>
                <span className='mr-1 font-semibold'>Path:</span>
                { questionPath(report.turns, c.competencyId).map((t, i) => {
                  const v = VERDICT_MARK[t.evaluation!.verdict];
                  return (
                    <span key={t.number} className='tabular-nums'>
                      { i > 0 && <span className='mx-1 text-slate-300'>→</span> }
                      L{ t.level } <span className={`font-semibold ${v.className}`}>{ v.mark }</span>
                    </span>
                  );
                }) }
                { c.probes === 0 && <span className='text-slate-400'>not reached</span> }
              </div>
              { c.evidence.length > 0 && (
                <ul className='mt-2 flex flex-col gap-1 text-sm text-slate-700'>
                  { c.evidence.map((e) => <li key={e} className='border-l-2 border-slate-200 pl-3 italic'>{ e }</li>) }
                </ul>
              ) }
              { c.concerns.length > 0 && (
                <ul className='mt-2 list-disc pl-5 text-sm text-amber-800'>{ c.concerns.map((e) => <li key={e}>{ e }</li>) }</ul>
              ) }
            </div>
          )) }
          { notInText.length > 0 && (
            <p className='text-xs text-slate-500'>
              Check in person (not assessable from written answers): { notInText.map((c) => c.name).join(', ') }.
            </p>
          ) }
        </section>
      ) }

      <section className='flex flex-col gap-3'>
        <h4 className='text-sm font-semibold text-slate-800'>Transcript</h4>
        { report.turns.map((t) => (
          <div key={t.number} className='rounded-xl border border-slate-100 p-4'>
            <div className='mb-1 flex flex-wrap items-center gap-2 text-xs text-slate-500'>
              <span className='font-semibold tabular-nums text-slate-700'>Q{ t.number }</span>
              { t.level && <span>asked at L{ t.level }</span> }
              { t.evaluation && (
                <span className={`font-semibold ${VERDICT_MARK[t.evaluation.verdict].className}`}>
                  { t.evaluation.verdict } · showed L{ t.evaluation.demonstratedLevel }
                </span>
              ) }
            </div>
            <p className='text-sm font-medium text-slate-900'>{ t.question }</p>
            { t.videoUrl && (
              <video src={t.videoUrl} controls preload='metadata' className='mt-3 aspect-video w-full max-w-xl rounded-lg bg-black' />
            ) }
            { t.answer !== undefined && t.answer !== null && (
              <div className='mt-2'>
                { t.videoUrl && <div className='text-[11px] font-semibold uppercase tracking-wide text-slate-400'>Transcript</div> }
                <p className='whitespace-pre-wrap text-sm text-slate-700'>{ t.answer || '(no speech detected)' }</p>
              </div>
            ) }
            { t.evaluation?.rationale && <p className='mt-2 text-xs text-slate-500'>{ t.evaluation.rationale }</p> }
          </div>
        )) }
      </section>
    </div>
  );
};
